import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type { Project, RunningTimer, StartTimerDto } from 'api';
import dayjs, { type Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { HeroContainer } from 'shared/components/HeroContainer';
import { formatDuration } from 'shared/lib/formatDuration';

import {
  DISCARD_POPOVER_PAPER_SX,
  DiscardCancelButton,
  DiscardConfirmButton,
  DiscardPopoverActions,
  DiscardPopoverBody,
  DiscardPopoverContent,
  DiscardPopoverTitle,
  HeroActionButton,
  HeroBillableLabel,
  HeroDescriptionInput,
  HeroDiscardButton,
  HeroDurationBadge,
  HeroGreeting,
  HeroInputRow,
  HeroLiveActions,
  HeroManualTimeRow,
  HeroModeToggleButton,
  HeroModeToggleWrapper,
  HeroProjectPickerWrapper,
  HeroStartTimePopoverContent,
  HeroSubtitle,
  HeroSwitch,
  HeroTimeBox,
  HeroTimerDisplay,
  HeroTopRow,
  StartTimeText,
  TimeInput,
} from '../tracker.styles';
import { buildDateTimeISO, parseTimeDigits, toHHMM } from '../utils/trackerUtils';

import { ProjectPicker } from './ProjectPicker';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const nowDigits = (offsetMinutes = 0) => {
  const d = new Date(Date.now() + offsetMinutes * 60 * 1000);
  return `${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`;
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface TimerBarProps {
  mode: 'live' | 'manual';
  projects: Project[];
  runningTimer: RunningTimer | null;
  onStart: (data: StartTimerDto) => void;
  onStop: (timeEntryId: string) => void;
  onDiscard: (timeEntryId: string) => void;
  onAddManual: (data: {
    projectId: string;
    description?: string;
    startedAt: string;
    endedAt: string;
    isBillable: boolean;
  }) => void;
  onUpdateDescription?: (description: string, isBillable: boolean) => void;
  onUpdateStartTime?: (startedAt: string, isBillable: boolean) => void;
  onModeChange: (mode: 'live' | 'manual') => void;
  isStarting: boolean;
  isStopping: boolean;
  resetKey?: number;
  userName?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const TimerBar: React.FC<TimerBarProps> = ({
  mode,
  projects,
  runningTimer,
  onStart,
  onStop,
  onDiscard,
  onAddManual,
  onUpdateDescription,
  onUpdateStartTime,
  onModeChange,
  isStarting,
  isStopping,
  resetKey,
  userName,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [isBillable, setIsBillable] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [draftStartTime, setDraftStartTime] = useState('');
  const [editingStartTime, setEditingStartTime] = useState(false);

  const [manualDate, setManualDate] = useState<Dayjs>(dayjs());
  const [startTime, setStartTime] = useState(nowDigits);
  const [endTime, setEndTime] = useState(() => nowDigits(1));
  const [editingManualStart, setEditingManualStart] = useState(false);
  const [editingManualEnd, setEditingManualEnd] = useState(false);
  const startTimeSnapshot = useRef(startTime);
  const endTimeSnapshot = useRef(endTime);
  const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(null);

  const timerDisplayRef = useRef<HTMLSpanElement>(null);
  const discardAnchorRef = useRef<HTMLButtonElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const savedDescriptionRef = useRef<string>('');

  useEffect(() => {
    if (runningTimer) {
      const startMs = new Date(runningTimer.startedAt).getTime();
      const initialElapsed = Math.max(
        0,
        Math.floor((runningTimer.clientReceivedAt - startMs) / 1000),
      );
      const receivedAt = runningTimer.clientReceivedAt;
      const tick = () => setElapsed(initialElapsed + Math.floor((Date.now() - receivedAt) / 1000));
      tick();
      intervalRef.current = setInterval(tick, 1000);
      if (runningTimer.projectId) setSelectedProjectId(runningTimer.projectId);
      setIsBillable(runningTimer.isBillable);
    } else {
      setElapsed(0);
      setSelectedProjectId(null);
      setIsBillable(false);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [runningTimer]);

  useEffect(() => {
    const desc = runningTimer?.description ?? '';
    setDescription(desc);
    savedDescriptionRef.current = desc;
  }, [runningTimer]);

  useEffect(() => {
    if (resetKey === undefined) return;
    setDescription('');
    setStartTime(nowDigits());
    setEndTime(nowDigits(1));
    setSelectedProjectId(null);
    setIsBillable(false);
    setEditingManualStart(false);
    setEditingManualEnd(false);
  }, [resetKey]);

  const handleDescriptionBlur = () => {
    if (runningTimer && onUpdateDescription && description !== savedDescriptionRef.current) {
      savedDescriptionRef.current = description;
      onUpdateDescription(description, isBillable);
    }
  };

  const handleStartTimeBlur = () => {
    if (!runningTimer || !onUpdateStartTime) return;
    const parsed = parseTimeDigits(draftStartTime);
    if (parsed === null) {
      if (draftStartTime.trim() !== '')
        enqueueSnackbar('Invalid start time', { variant: 'warning' });
      setDraftStartTime(toHHMM(runningTimer.startedAt).replace(':', ''));
      return;
    }
    if (parsed === toHHMM(runningTimer.startedAt)) return;
    const toDateStr = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const now = new Date();
    const candidate = new Date(buildDateTimeISO(toDateStr(now), parsed));
    if (candidate > now) {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      onUpdateStartTime(buildDateTimeISO(toDateStr(yesterday), parsed), isBillable);
    } else {
      onUpdateStartTime(candidate.toISOString(), isBillable);
    }
  };

  const handleStartStop = () => {
    if (runningTimer) {
      onStop(runningTimer.timeEntryId);
    } else if (selectedProjectId) {
      onStart({
        projectId: selectedProjectId,
        description: description.trim() || undefined,
        isBillable,
      });
    }
  };

  const handleAddManual = () => {
    if (!selectedProjectId) return;
    const dateStr = manualDate.format('YYYY-MM-DD');
    const parsedStart = parseTimeDigits(startTime);
    const parsedEnd = parseTimeDigits(endTime);
    if (!parsedStart) {
      enqueueSnackbar('Invalid start time', { variant: 'warning' });
      return;
    }
    if (!parsedEnd) {
      enqueueSnackbar('Invalid end time', { variant: 'warning' });
      return;
    }
    const startedAt = buildDateTimeISO(dateStr, parsedStart);
    const endedAt = buildDateTimeISO(dateStr, parsedEnd);
    if (new Date(endedAt) <= new Date(startedAt)) {
      enqueueSnackbar('End time must be after start time.', { variant: 'error' });
      return;
    }
    onAddManual({
      projectId: selectedProjectId,
      description: description.trim() || undefined,
      startedAt,
      endedAt,
      isBillable,
    });
  };

  const commitManualTime = (field: 'start' | 'end') => {
    if (field === 'start') {
      const parsed = parseTimeDigits(startTime);
      if (parsed === null && startTime.trim() !== '')
        enqueueSnackbar('Invalid start time', { variant: 'warning' });
      setStartTime(parsed !== null ? parsed.replace(':', '') : startTimeSnapshot.current);
      setEditingManualStart(false);
    } else {
      const parsed = parseTimeDigits(endTime);
      if (parsed === null && endTime.trim() !== '')
        enqueueSnackbar('Invalid end time', { variant: 'warning' });
      setEndTime(parsed !== null ? parsed.replace(':', '') : endTimeSnapshot.current);
      setEditingManualEnd(false);
    }
  };

  const tomorrow = dayjs().add(1, 'day');
  const isDateValid = !manualDate.isAfter(tomorrow, 'day');
  const canSubmit = Boolean(selectedProjectId) && isDateValid;

  const displayStart = parseTimeDigits(startTime) ?? startTime;
  const displayEnd = parseTimeDigits(endTime) ?? endTime;

  const manualDuration = (() => {
    const ps = parseTimeDigits(startTime);
    const pe = parseTimeDigits(endTime);
    if (!ps || !pe) return '';
    const dateStr = manualDate.format('YYYY-MM-DD');
    const diff =
      (new Date(buildDateTimeISO(dateStr, pe)).getTime() -
        new Date(buildDateTimeISO(dateStr, ps)).getTime()) /
      1000;
    return diff > 0 ? formatDuration(diff) : '';
  })();

  const greeting = `${getGreeting()}${userName ? `, ${userName}` : ''}`;

  return (
    <>
      <HeroContainer padding="36px 24px" style={{ marginBottom: 20 }}>
        {/* Top row: greeting + mode toggle */}
        <HeroTopRow>
          <div>
            <HeroGreeting>{greeting} 👋</HeroGreeting>
            <HeroSubtitle>What are you working on?</HeroSubtitle>
          </div>
          <HeroModeToggleWrapper>
            <HeroModeToggleButton
              active={mode === 'live' ? 1 : 0}
              onClick={() => onModeChange('live')}
            >
              Live Tracking
            </HeroModeToggleButton>
            <HeroModeToggleButton
              active={mode === 'manual' ? 1 : 0}
              onClick={() => onModeChange('manual')}
            >
              Manual Entry
            </HeroModeToggleButton>
          </HeroModeToggleWrapper>
        </HeroTopRow>

        {/* Input row */}
        <HeroInputRow>
          <HeroDescriptionInput
            placeholder="e.g. Redesign onboarding flow"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={handleDescriptionBlur}
          />

          <HeroProjectPickerWrapper>
            <ProjectPicker
              projects={projects}
              selectedId={selectedProjectId}
              onSelect={(p) => {
                setSelectedProjectId(p.id);
                setIsBillable(p.isBillable);
              }}
            />
          </HeroProjectPickerWrapper>

          <HeroLiveActions>
            <HeroSwitch
              size="small"
              checked={isBillable}
              onChange={(e) => !runningTimer && setIsBillable(e.target.checked)}
            />
            <HeroBillableLabel>Billable</HeroBillableLabel>
          </HeroLiveActions>

          {mode === 'live' ? (
            <>
              <span ref={timerDisplayRef}>
                <HeroTimerDisplay
                  onClick={() => {
                    if (!runningTimer) return;
                    setDraftStartTime(toHHMM(runningTimer.startedAt).replace(':', ''));
                    setEditingStartTime(true);
                  }}
                  sx={{ cursor: runningTimer ? 'pointer' : 'default' }}
                >
                  {formatDuration(elapsed)}
                </HeroTimerDisplay>
              </span>

              {runningTimer && (
                <Popover
                  open={editingStartTime}
                  anchorEl={timerDisplayRef.current}
                  onClose={() => setEditingStartTime(false)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                  <HeroStartTimePopoverContent>
                    <StartTimeText>Start Time</StartTimeText>
                    <TimeInput
                      value={draftStartTime}
                      onChange={(e) => setDraftStartTime(e.target.value)}
                      onBlur={() => {
                        handleStartTimeBlur();
                        setEditingStartTime(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleStartTimeBlur();
                          setEditingStartTime(false);
                        } else if (e.key === 'Escape') setEditingStartTime(false);
                      }}
                      inputProps={{ maxLength: 5 }}
                      autoFocus
                    />
                    <StartTimeText>
                      {new Date(runningTimer.startedAt).toLocaleDateString('en-GB')}
                    </StartTimeText>
                  </HeroStartTimePopoverContent>
                </Popover>
              )}

              {runningTimer && (
                <Tooltip title="Discard">
                  <HeroDiscardButton
                    ref={discardAnchorRef}
                    onClick={() => setDiscardConfirmOpen(true)}
                    disabled={isStopping}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                  </HeroDiscardButton>
                </Tooltip>
              )}

              <Tooltip title={!runningTimer && !canSubmit ? 'Select a project first' : ''}>
                <span>
                  <HeroActionButton
                    disableElevation
                    stopmode={runningTimer ? 1 : 0}
                    onClick={handleStartStop}
                    disabled={isStarting || isStopping || (!runningTimer && !canSubmit)}
                  >
                    {runningTimer ? (
                      <>
                        <StopIcon sx={{ fontSize: 15, mr: 0.5 }} />
                        Stop
                      </>
                    ) : (
                      <>
                        <PlayArrowIcon sx={{ fontSize: 15, mr: 0.5 }} />
                        Start
                      </>
                    )}
                  </HeroActionButton>
                </span>
              </Tooltip>
            </>
          ) : (
            <Tooltip title={!canSubmit ? 'Select a project first' : ''}>
              <span>
                <HeroActionButton
                  disableElevation
                  onClick={handleAddManual}
                  disabled={isStarting || !canSubmit}
                >
                  + Add entry
                </HeroActionButton>
              </span>
            </Tooltip>
          )}
        </HeroInputRow>

        {/* Manual mode: date + time row */}
        {mode === 'manual' && (
          <HeroManualTimeRow>
            <HeroTimeBox onClick={(e) => setCalendarAnchor(e.currentTarget)}>
              <span>{manualDate.format('DD-MM-YYYY')}</span>
              <CalendarTodayIcon sx={{ fontSize: 13, opacity: 0.6 }} />
            </HeroTimeBox>

            <Popover
              open={Boolean(calendarAnchor)}
              anchorEl={calendarAnchor}
              onClose={() => setCalendarAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateCalendar
                  value={manualDate}
                  maxDate={tomorrow}
                  onChange={(v) => {
                    if (v) {
                      setManualDate(v);
                      setCalendarAnchor(null);
                    }
                  }}
                />
              </LocalizationProvider>
            </Popover>

            <HeroTimeBox
              onClick={() => {
                startTimeSnapshot.current = startTime;
                setEditingManualStart(true);
              }}
            >
              {editingManualStart ? (
                <input
                  autoFocus
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  onBlur={() => commitManualTime('start')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitManualTime('start');
                    if (e.key === 'Escape') {
                      setStartTime(startTimeSnapshot.current);
                      setEditingManualStart(false);
                    }
                  }}
                  maxLength={5}
                />
              ) : (
                <>
                  <span>{displayStart}</span>
                  <AccessTimeIcon sx={{ fontSize: 12, opacity: 0.5 }} />
                </>
              )}
            </HeroTimeBox>

            <HeroTimeBox
              onClick={() => {
                endTimeSnapshot.current = endTime;
                setEditingManualEnd(true);
              }}
            >
              {editingManualEnd ? (
                <input
                  autoFocus
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  onBlur={() => commitManualTime('end')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitManualTime('end');
                    if (e.key === 'Escape') {
                      setEndTime(endTimeSnapshot.current);
                      setEditingManualEnd(false);
                    }
                  }}
                  maxLength={5}
                />
              ) : (
                <>
                  <span>{displayEnd}</span>
                  <AccessTimeIcon sx={{ fontSize: 12, opacity: 0.5 }} />
                </>
              )}
            </HeroTimeBox>

            {manualDuration && <HeroDurationBadge>{manualDuration}</HeroDurationBadge>}
          </HeroManualTimeRow>
        )}
      </HeroContainer>

      <Popover
        open={discardConfirmOpen}
        anchorEl={discardAnchorRef.current}
        onClose={() => setDiscardConfirmOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: DISCARD_POPOVER_PAPER_SX } }}
      >
        <DiscardPopoverContent>
          <DiscardPopoverTitle>Discard timer?</DiscardPopoverTitle>
          <DiscardPopoverBody>This will delete the running entry.</DiscardPopoverBody>
          <DiscardPopoverActions>
            <DiscardCancelButton size="small" onClick={() => setDiscardConfirmOpen(false)}>
              Cancel
            </DiscardCancelButton>
            <DiscardConfirmButton
              size="small"
              variant="contained"
              color="error"
              onClick={() => {
                if (runningTimer) onDiscard(runningTimer.timeEntryId);
                setDiscardConfirmOpen(false);
              }}
            >
              Discard
            </DiscardConfirmButton>
          </DiscardPopoverActions>
        </DiscardPopoverContent>
      </Popover>
    </>
  );
};
